import React, { useState, useRef, useEffect } from 'react';

interface ElevationData {
  elevationGain?: number;
  elevationLoss?: number;
  gpsData?: Array<{
    latitude?: number;
    longitude?: number;
    altitude?: number;
    timestamp: number;
    distanceFromStart?: number;
  }>;
}

interface ExpandableElevationProps {
  data: ElevationData;
}

/**
 * Haversine formula — cumulative distance from GPS points (meters).
 * Only used when distanceFromStart is not already provided.
 */
const cumulativeDistances = (
  points: Array<{ latitude?: number; longitude?: number; altitude?: number }>
): number[] => {
  const distances = [0];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    if (prev.latitude != null && prev.longitude != null && curr.latitude != null && curr.longitude != null) {
      const R = 6371000;
      const dLat = ((curr.latitude - prev.latitude) * Math.PI) / 180;
      const dLon = ((curr.longitude - prev.longitude) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((prev.latitude * Math.PI) / 180) *
          Math.cos((curr.latitude * Math.PI) / 180) *
          Math.sin(dLon / 2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      distances.push(distances[i - 1] + R * c);
    } else {
      distances.push(distances[i - 1]);
    }
  }
  return distances;
};

export const ExpandableElevation: React.FC<ExpandableElevationProps> = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!isExpanded || !canvasRef.current || !data.gpsData) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Filter points with altitude data
    if (!data.gpsData || !Array.isArray(data.gpsData)) return;
    const pointsWithAltitude = data.gpsData.filter(p => p.altitude !== undefined);
    if (pointsWithAltitude.length < 2) return;

    // Build distance array — prefer pre-computed distanceFromStart, fallback to Haversine
    let distances: number[];
    if (pointsWithAltitude[0].distanceFromStart !== undefined) {
      distances = pointsWithAltitude.map(p => p.distanceFromStart ?? 0);
    } else if (pointsWithAltitude[0].latitude !== undefined) {
      distances = cumulativeDistances(pointsWithAltitude);
    } else {
      // Pure index-based fallback
      distances = pointsWithAltitude.map((_, i) => i);
    }

    const totalDistanceM = distances[distances.length - 1];
    const totalDistanceKm = totalDistanceM / 1000;
    const useKm = totalDistanceM > 500; // show km labels for > 500 m

    const width = canvas.width;
    const height = canvas.height;
    const padding = { top: 20, right: 20, bottom: 35, left: 50 };
    const chartW = width - padding.left - padding.right;
    const chartH = height - padding.top - padding.bottom;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Get altitude range
    const altitudes = pointsWithAltitude.map(p => p.altitude!);
    const minAlt = Math.min(...altitudes);
    const maxAlt = Math.max(...altitudes);
    const altRange = Math.max(maxAlt - minAlt, 10); // guard against flat-line activities

    // Scale functions
    const scaleX = (dist: number) =>
      padding.left + (dist / (totalDistanceM || 1)) * chartW;
    const scaleY = (alt: number) =>
      padding.top + (1 - (alt - minAlt) / altRange) * chartH;

    // ── Grid lines (horizontal) ──
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= 4; i++) {
      const y = padding.top + (i / 4) * chartH;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();

      const altValue = maxAlt - (i / 4) * altRange;
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`${Math.round(altValue)}m`, padding.left - 6, y + 3);
    }

    // ── X-axis labels (distance) ──
    const numXLabels = Math.min(5, Math.floor(totalDistanceKm) || 1);
    ctx.textAlign = 'center';
    for (let i = 0; i <= numXLabels; i++) {
      const dist = (i / numXLabels) * totalDistanceM;
      const x = scaleX(dist);
      // Tick
      ctx.beginPath();
      ctx.moveTo(x, padding.top + chartH);
      ctx.lineTo(x, padding.top + chartH + 4);
      ctx.strokeStyle = '#6B7280';
      ctx.lineWidth = 1;
      ctx.stroke();
      // Label
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '10px sans-serif';
      const label = useKm ? `${(dist / 1000).toFixed(1)} km` : `${Math.round(dist)} m`;
      ctx.fillText(label, x, padding.top + chartH + 16);
    }

    // ── Gradient fill under the curve (Cedar brand: Cedar Prime → Cedar Stream) ──
    const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartH);
    gradient.addColorStop(0, 'rgba(31, 108, 79, 0.25)');   // Cedar Prime at top (high elevation)
    gradient.addColorStop(1, 'rgba(66, 165, 165, 0.08)');  // Cedar Stream at bottom (low elevation)

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(scaleX(distances[0]), padding.top + chartH);
    pointsWithAltitude.forEach((point, index) => {
      ctx.lineTo(scaleX(distances[index]), scaleY(point.altitude!));
    });
    ctx.lineTo(scaleX(distances[distances.length - 1]), padding.top + chartH);
    ctx.closePath();
    ctx.fill();

    // ── Elevation line with incline/decline coloring ──
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    for (let i = 1; i < pointsWithAltitude.length; i++) {
      const prevAlt = pointsWithAltitude[i - 1].altitude!;
      const currAlt = pointsWithAltitude[i].altitude!;
      // Green for uphill, red for downhill, blue for flat
      const diff = currAlt - prevAlt;
      if (diff > 0.5) {
        ctx.strokeStyle = '#1F6C4F'; // Cedar Prime — climbing
      } else if (diff < -0.5) {
        ctx.strokeStyle = '#EF4444'; // Semantic error red — descending
      } else {
        ctx.strokeStyle = '#42A5A5'; // Cedar Stream — flat
      }
      ctx.beginPath();
      ctx.moveTo(scaleX(distances[i - 1]), scaleY(prevAlt));
      ctx.lineTo(scaleX(distances[i]), scaleY(currAlt));
      ctx.stroke();
    }

    // ── Min / Max altitude markers ──
    const minIdx = altitudes.indexOf(minAlt);
    const maxIdx = altitudes.indexOf(maxAlt);

    // Max altitude dot — Cedar Prime (climbing)
    ctx.fillStyle = '#1F6C4F';
    ctx.beginPath();
    ctx.arc(scaleX(distances[maxIdx]), scaleY(maxAlt), 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#A8D1BF'; // Cedar Mist — legible on dark canvas background
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center'; // explicit reset
    ctx.fillText(`${Math.round(maxAlt)}m`, scaleX(distances[maxIdx]), scaleY(maxAlt) - 8);

    // Min altitude dot — Cedar Stream (flat/low)
    ctx.fillStyle = '#42A5A5';
    ctx.beginPath();
    ctx.arc(scaleX(distances[minIdx]), scaleY(minAlt), 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#A8D1BF'; // Cedar Mist
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center'; // explicit reset
    ctx.fillText(`${Math.round(minAlt)}m`, scaleX(distances[minIdx]), scaleY(minAlt) + 14);

  }, [isExpanded, data]);

  const hasElevationData = (data.elevationGain != null && data.elevationGain > 0) ||
                          (data.elevationLoss != null && data.elevationLoss > 0);

  if (!hasElevationData) {
    return null;
  }

  return (
    <details
      className="w-full max-w-2xl mx-auto mb-4"
      open={isExpanded}
      onToggle={(e) => setIsExpanded((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-center text-sm font-medium text-blue-400 hover:text-blue-300 py-2 px-4 bg-gray-800 rounded-lg transition-colors">
        Show Elevation Profile ▼
      </summary>

      <div className="mt-3 bg-gray-800 rounded-lg p-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          {data.elevationGain && data.elevationGain > 0 && (
            <div className="text-center">
              <div className="text-sm text-gray-400 mb-1">Elevation Gain</div>
              <div className="text-2xl font-bold text-green-400">
                ↑ {Math.round(data.elevationGain)}m
              </div>
            </div>
          )}
          {data.elevationLoss && data.elevationLoss > 0 && (
            <div className="text-center">
              <div className="text-sm text-gray-400 mb-1">Elevation Loss</div>
              <div className="text-2xl font-bold text-red-400">
                ↓ {Math.round(data.elevationLoss)}m
              </div>
            </div>
          )}
        </div>

        {/* Elevation Profile Chart */}
        {data.gpsData && data.gpsData.some(p => p.altitude !== undefined) && (
          <div className="mt-4">
            <canvas
              ref={canvasRef}
              width={600}
              height={220}
              className="w-full bg-gray-900 rounded-lg"
            />
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mt-2">
              <span style={{ color: '#1F6C4F' }}>● Climbing</span>
              <span style={{ color: '#42A5A5' }}>● Flat</span>
              <span style={{ color: '#EF4444' }}>● Descending</span>
            </div>
          </div>
        )}
      </div>
    </details>
  );
};
